import { logSwallowed } from '@/lib/errors';
// ============================================================================
// swUpdate.ts — обновление фронта (PWA) и Web Push (SRP/SSoT):
// 1. notifyAppUpdated — новая версия фронта (SW прислал HYDRA_APP_UPDATED) →
//    показываем баннер «Перезагрузить для обновления» (один раз за сессию).
// 2. Web Push: подписка браузера (PushManager) → сервер (PushService RPC),
//    отписка, обработка push-сообщений (показ уведомления — в sw.js).
// ============================================================================

import { create } from 'zustand';
import { createLogger } from '@/lib/logger';
import { getDeploymentMode, isContour } from '@/lib/deployment';
import { isWailsMode } from '@/lib/wailsBridge';

const log = createLogger('swUpdate');

export interface SwUpdateState {
  /** true — доступна новая версия фронта, нужна перезагрузка */
  updateAvailable: boolean;
  version: string;
}

export const useSwUpdateStore = create<SwUpdateState>(() => ({
  updateAvailable: false,
  version: '',
}));

/** SW сообщил о новой версии фронта (HYDRA_APP_UPDATED). */
let notifiedVersion = ''; // защита от дублей (SW + checkBundleVersion + серверная версия)

/**
 * ПУБЛИЧНЫЙ САЙТ (/p/...) — посетитель не «в приложении»: уведомление об
 * обновлении фронта там НЕ показываем и проверку бандла не выполняем
 * (перезагрузка несообразна, а сообщение путает). В приложении и на страницах
 * приложения (/, /designer, /enterprise, /remote, /confirm...) — как раньше.
 */
function isPublicSitePath(): boolean {
  return typeof window !== 'undefined' && window.location.pathname.startsWith('/p/');
}

export function notifyAppUpdated(version: string): void {
  // DESKTOP (Wails): фронт ВШИТ в exe и обновляется вместе с ним. Сравнение с
  // бандлом сервера всегда расходится → баннер звал бы «Перезагрузить», а
  // location.reload() в WebView2 ЛОМАЕТ Wails-мост (приложение уходит в web-режим
  // → «сервер недоступен»). В десктопе баннер обновления НЕ показываем вообще.
  if (isWailsMode()) return;
  // Публичный сайт: уведомление обновления фронта не показываем (см. выше).
  if (isPublicSitePath()) return;
  // Закрытый контур: обновления фронта извне недоступны — уведомление не показываем.
  if (isContour(getDeploymentMode())) return;
  if (notifiedVersion) return; // уже показали уведомление за эту сессию
  notifiedVersion = version;
  useSwUpdateStore.setState({ updateAvailable: true, version });
  // ЕДИНЫЙ механизм уведомлений (высший приоритет): в колокольчик (priority →
  // спецраздел) + ТОСТ с действием «Перезагрузить». Баннер-перекрытие НЕ
  // используем (перекрывается окнами/модалками — пользователь его не видит).
  const reload = () => reloadForUpdate();
  void Promise.all([
    import('@/store/toastStore'),
    import('@/lib/i18n/store'),
  ]).then(([{ useToastStore }, { translate }]) => {
    useToastStore.getState().showToast(translate('update.new_version_available'), 'info', {
      duration: 0,
      priority: true,
      details: { action: { label: translate('update.reload'), onClick: reload } },
    });
  });
}

/**
 * ДЕТЕРМИНИРОВАННАЯ проверка «на сервере собран более новый бандл»:
 * сравниваем имя entry-чанка в свежем index.html (cache: no-store) с
 * фактически загруженным скриптом страницы. Не зависит от SW-гонок —
 * сообщение HYDRA_APP_UPDATED терялось, если SW успевал активироваться
 * ДО подписки клиента на message (страница открыта после деплоя), и
 * не приходило вовсе, пока вкладка жила без перезагрузки.
 * Вызывается при старте и по событиям focus/visibility/online.
 */
let bundleCheckInFlight = false;
export async function checkBundleVersion(): Promise<void> {
  // Публичный сайт: проверку бандла не выполняем вовсе (см. isPublicSitePath).
  if (isPublicSitePath()) return;
  // Закрытый контур: проверка внешнего бандла не выполняется.
  if (isContour(getDeploymentMode())) return;
  if (bundleCheckInFlight) return;
  bundleCheckInFlight = true;
  try {
    const res = await fetch('/', { cache: 'no-store' });
    if (!res.ok) return;
    const html = await res.text();
    const m = html.match(/assets\/(index-[^"']+\.js)/);
    if (!m) return;
    const serverEntry = m[1];
    const currentSrc = document
      .querySelector('script[src*="assets/index-"]')
      ?.getAttribute('src');
    const currentEntry = currentSrc?.split('/').pop();
    if (
      currentEntry &&
      currentEntry !== serverEntry &&
      !useSwUpdateStore.getState().updateAvailable
    ) {
      log.info('newer bundle on server: ' + serverEntry + ' (loaded: ' + currentEntry + ')');
      notifyAppUpdated(serverEntry);
    }
  } catch (e) {
    // Офлайн — проверить нечего; нормальное состояние, НЕ ошибка.
    log.debug('bundle version check skipped', e);
  } finally {
    bundleCheckInFlight = false;
  }
}

/** Пользователь нажал «Перезагрузить» — обновляем страницу (новый SW уже активен). */
export function reloadForUpdate(): void {
  // DESKTOP (Wails): перезагрузка страницы уничтожает window.go (мост) — НЕ
  // перезагружаем; десктоп обновляется заменой exe.
  if (isWailsMode()) return;
  window.location.reload();
}

/**
 * СЕРВЕР сообщает клиентам актуальную версию фронта (retained-событие
 * platform/frontend_version, публикуется при старте сервера). Каждый клиент при
 * ПОДКЛЮЧЕНИИ стрима EventBus (Subscribe '#') сразу получает retained → сравниваем
 * со своим entry-чанком → не совпало → баннер «Перезагрузить». Это работает для
 * ЛЮБОГО клиента, открытого днями: переподключение стрима (автореконнект/фокус/
 * online) приносит актуальную версию — НЕ полагаемся на SW-гонки/focus/pageshow.
 * Подписка вешается один раз (main.tsx); eventBusHub сам раздаст после входа.
 */
export function subscribeServerFrontendVersion(): void {
  // Ленивый импорт — избегаем цикла eventBusHub ↔ swUpdate (hub импортирует sdkStore).
  // Подписка живёт всё время сессии (как у других подписчиков hub) — отписка не нужна.
  void import('@/lib/eventBusHub').then(({ eventBusHub }) => {
    eventBusHub.subscribe((ev) => {
      if (ev.topic !== 'platform/frontend_version' && ev.topic !== 'platform.frontend_version') return;
      const serverVersion = String(ev.payload?.version || '');
      if (!serverVersion) return;
      const currentSrc = document.querySelector('script[src*="assets/index-"]')?.getAttribute('src');
      const currentEntry = currentSrc?.split('/').pop() || '';
      if (currentEntry && currentEntry !== serverVersion && !useSwUpdateStore.getState().updateAvailable) {
        log.info(`server frontend version: ${serverVersion} (loaded: ${currentEntry})`);
        notifyAppUpdated(serverVersion);
      }
    });
  }).catch((e) => { logSwallowed(e, 'lib/swUpdate.ts:142'); return undefined; });
}

// ============================================================================
// Web Push
// ============================================================================

const PUSH_KEY = 'hydra_push_enabled';

/** PushManager доступен (HTTPS/secure context) и поддерживается средой.
 * WebView2 (Wails desktop) НЕ поддерживает Web Push — честно отключаем:
 * push-уведомления работают в браузере/PWA (Chrome/Edge), не в desktop-exe. */
export function isPushSupported(): boolean {
  if (typeof navigator === 'undefined' || !window.isSecureContext) return false;
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  try {
    if (navigator.userAgent.includes('Wails')) return false;
  } catch { /* ignore */ }
  return true;
}

export function isPushEnabled(): boolean {
  try {
    return localStorage.getItem(PUSH_KEY) === '1';
  } catch {
    return false;
  }
}

export function setPushEnabled(v: boolean): void {
  try {
    if (v) localStorage.setItem(PUSH_KEY, '1');
    else localStorage.removeItem(PUSH_KEY);
  } catch { /* ignore */ }
  // Той же вкладке (storage-событие срабатывает только в других окнах)
  try {
    window.dispatchEvent(new CustomEvent('hydra:push-changed'));
  } catch { /* ignore */ }
}

/**
 * Подписка на Web Push:
 * 1. Запрашиваем разрешение (Notification.requestPermission).
 * 2. pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: vapidKey }).
 * 3. Отправляем подписку на сервер (PushService.Subscribe).
 */
export async function subscribePush(vapidKey: string, baseUrl: string): Promise<boolean> {
  if (!isPushSupported()) return false;
  const registration = await navigator.serviceWorker.ready;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const sub = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    // applicationServerKey: ArrayBuffer ровно 65 байт (Uint8Array без byteOffset)
    applicationServerKey: urlBase64ToUint8Array(vapidKey).buffer as ArrayBuffer,
  });

  // Валидация подписки (WebView2 мог вернуть «пустышку») — иначе сервер
  // ответит «subscription required»
  const p256dh = sub.getKey('p256dh');
  const auth = sub.getKey('auth');
  if (!sub.endpoint || !p256dh || !auth) {
    setPushEnabled(false);
    return false;
  }

  // SSoT: сервер хранит подписку (endpoint + keys P256DH/Auth).
  // ВАЖНО: proto PushSubscribeRequest{ subscription = 1 } — subscription
  // ВЛОЖЕННЫЙ объект; иначе сервер отвечает «subscription required» и
  // подписка НЕ сохраняется (push «тишина»).
  const sdk = await import('@/store/sdkStore').then((m) => m.getSDK());
  await sdk.push.subscribe({
    subscription: {
      endpoint: sub.endpoint,
      p256dh: btoa(String.fromCharCode(...new Uint8Array(p256dh))),
      auth: btoa(String.fromCharCode(...new Uint8Array(auth))),
    },
  } as any);

  setPushEnabled(true);
  return true;
}

/** Отписка: pushManager.unsubscribe + PushService.Unsubscribe. */
export async function unsubscribePush(): Promise<void> {
  setPushEnabled(false);
  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    if (sub) {
      const sdk = await import('@/store/sdkStore').then((m) => m.getSDK());
      try {
        await sdk.push.unsubscribe({ endpoint: sub.endpoint } as any);
      } catch { /* сервер может быть недоступен — локальная отписка всё равно нужна */ }
      await sub.unsubscribe();
    }
  } catch { /* ignore */ }
}

/** VAPID-ключ (base64url) → Uint8Array<ArrayBuffer> для PushManager.subscribe. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const buf = new ArrayBuffer(raw.length);
  const output = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}
